import React from 'react';

export interface BadgeProps {
  /** Badge variant */
  variant?: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info' | 'neutral';
  /** Badge size */
  size?: 'sm' | 'md' | 'lg';
  /** Show dot indicator before text */
  dot?: boolean;
  /** Pill shape */
  pill?: boolean;
  /** Additional class names */
  className?: string;
  /** Badge content */
  children: React.ReactNode;
}

const variantStyles = {
  primary: 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400',
  secondary: 'bg-secondary-100 text-secondary-700 dark:bg-secondary-900/30 dark:text-secondary-400',
  success: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  warning: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  error: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  info: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  neutral: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
};

const sizeStyles = {
  sm: 'px-2 py-0.5 text-xs',
  md: 'px-2.5 py-1 text-xs',
  lg: 'px-3 py-1.5 text-sm',
};

/**
 * Badge component for status indicators and labels
 */
const Badge: React.FC<BadgeProps> = ({
  variant = 'neutral',
  size = 'md',
  dot = false,
  pill = true,
  className = '',
  children,
}) => {
  const classes = [
    'inline-flex items-center gap-1.5 font-medium',
    variantStyles[variant],
    sizeStyles[size],
    pill ? 'rounded-full' : 'rounded-md',
    className,
  ]
    .join(' ')
    .trim();

  return (
    <span className={classes}>
      {dot && <span className='w-1.5 h-1.5 rounded-full bg-current' />}
      {children}
    </span>
  );
};

export default Badge;
